// featureFlagsContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { SUBSCRIPTION_TIERS } from './subscriptionTiers';

// Every feature defaults to enabled except multiCompany — a brand-new company (no flags doc
// yet) starts with exactly one company, matching how a newly signed-up customer's account
// actually begins; they opt into Multi-Company Support (and set up their second company)
// explicitly through Features & Plugins rather than finding it already on.
export const DEFAULT_FLAGS = {
  gpsTracking: true,
  multiCompany: false,
  emailMonitoring: true,
  sheetsMonitoring: true,
  csvImport: true,
  communication: true,
  invoicing: true,
  quickbooks: false,
  // Off by default — needs an Anthropic API key configured (Settings → Integrations) and each
  // import has a real per-call cost, so it shouldn't just appear unasked either.
  aiCatalogImport: false,
  // AI Drafting Assistant (notes, line-item descriptions, job summaries) — off by default for
  // the same per-call-cost reason as aiCatalogImport. Admin/Coordinator get it automatically
  // once this is on. `aiAssistantFieldRolesEnabled` (an admin opts in separately if they also
  // want Technicians to have it) is deliberately NOT listed here — it's a sub-choice under
  // this feature rather than something sold/locked on its own, so it's read/written straight off
  // rawFlags below instead of going through tier-lock/platform-override logic meant for
  // standalone features.
  aiAssistant: false
};

const FeatureFlagsContext = createContext(null);

// One real-time listener shared by the whole app (technician and admin branches alike),
// matching the CompanyProvider/useCompany() pattern in companyContext.js — every consumer
// reads live flags without its own fetch or prop-drilling. Scoped per company (doc id
// `{companyId}_features` in appSettings — see firestore.rules) rather than one global doc
// shared by every company in the deployment, so a company signing up for its own account never
// sees or changes another company's plugin toggles. `companyId` is the caller's PRIMARY company
// (App.js's companyIds[0]) — a user's other companies under Multi-Company Support intentionally
// keep sharing that one anchor company's flags, same as today; only genuinely separate,
// independently-signed-up companies (a different companyIds[0] entirely) are isolated from
// each other.
export function FeatureFlagsProvider({ companyId, children }) {
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);
  // Only the two subscription fields are read here — a separate, lightweight listener rather
  // than reusing companyContext.js's useCompany(), since this provider wraps ALL THREE
  // dashboards (App.js) while CompanyProvider only exists inside the admin Dashboard's own
  // subtree; there's no ancestor of this provider that could supply it via context instead.
  const [subscription, setSubscription] = useState({ tier: null, status: null, trialTier: null, trialEndsAt: null, compedTier: null });
  // Platform-wide overrides (MasterAdminConsole.js's kill-switch tab) — a single singleton doc,
  // not scoped per company like `flags` above. { [key]: true } means that flag is forced off for
  // every company regardless of what they've configured; a key simply absent (or false) means no
  // override, so un-killing a flag reverts each company to whatever it already had saved rather
  // than turning it back on everywhere.
  const [platformOverrides, setPlatformOverrides] = useState({});
  // Per-company platform override (MasterAdminConsole.js's Features panel) — scoped by companyId
  // like `flags` above, NOT a singleton like platformOverrides. { [key]: 'on' | 'off' }; a key
  // simply absent means no override, normal tier/toggle logic applies. Distinct from the global
  // kill switch above: this targets one specific company rather than every company at once, and
  // (unlike the kill switch, which can only ever force a flag off) can also force a flag ON beyond
  // what a company's own subscription tier would otherwise allow.
  const [companyFeatureOverrides, setCompanyFeatureOverrides] = useState({});

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'appSettings', 'platformKillSwitch'),
      (snap) => setPlatformOverrides(snap.exists() ? snap.data() : {}),
      () => setPlatformOverrides({})
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!companyId) {
      setFlags(DEFAULT_FLAGS);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const flagsDoc = doc(db, 'appSettings', `${companyId}_features`);
    const unsubscribe = onSnapshot(
      flagsDoc,
      (snap) => {
        setFlags({ ...DEFAULT_FLAGS, ...(snap.exists() ? snap.data() : {}) });
        setLoading(false);
      },
      () => {
        // Permission-denied or offline: fall back to defaults rather than leaving every
        // gated feature stuck in a "loading" state.
        setFlags(DEFAULT_FLAGS);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      setCompanyFeatureOverrides({});
      return undefined;
    }
    const unsubscribe = onSnapshot(
      doc(db, 'appSettings', `${companyId}_featureOverrides`),
      (snap) => setCompanyFeatureOverrides(snap.exists() ? snap.data() : {}),
      () => setCompanyFeatureOverrides({})
    );
    return unsubscribe;
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      setSubscription({ tier: null, status: null, trialTier: null, trialEndsAt: null, compedTier: null });
      return undefined;
    }
    const companyDoc = doc(db, 'companies', companyId);
    const unsubscribe = onSnapshot(
      companyDoc,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setSubscription({
          tier: data.subscriptionTier || null,
          status: data.subscriptionStatus || null,
          trialTier: data.trialTier || null,
          trialEndsAt: data.trialEndsAt || null,
          // Platform-admin-granted permanent free tier (functions/masterAdmin.js's
          // startCompanyComp/endCompanyComp) — only ever read when isComped is true, so a
          // leftover compedTier from a previously-ended comp is never mistaken for an active one.
          compedTier: data.isComped ? (data.compedTier || null) : null
        });
      },
      () => setSubscription({ tier: null, status: null, trialTier: null, trialEndsAt: null, compedTier: null })
    );
    return unsubscribe;
  }, [companyId]);

  const setFlag = async (key, value) => {
    if (!companyId) return;
    await setDoc(doc(db, 'appSettings', `${companyId}_features`), { companyId, [key]: value }, { merge: true });
  };

  // A live trial (functions/masterAdmin.js's startCompanyTrial) unlocks that tier's features
  // regardless of subscriptionStatus, independent of the real-subscription check below (a
  // company can hold a real subscription and ALSO be trialing a higher tier at once). Neither
  // a tier on record NOR a trial (active or merely un-cleared past its end date) at all ->
  // unrestricted (today's behavior, unchanged) — tier gating only ever applies once a company
  // has actually engaged with tiers at all, whether by subscribing or by receiving a trial. A
  // real tier that's fallen out of 'active' status (past_due/canceled) reverts to locked rather
  // than unrestricted; an unrecognized tier id fails open (unrestricted) rather than silently
  // locking everyone out over a typo or a not-yet-configured tier.
  //
  // A comp (functions/masterAdmin.js's startCompanyComp) is a third, independent path — a
  // platform-admin-granted permanent free tier, never expiring, coexisting with whatever real
  // subscriptionTier/Status Stripe has on record rather than replacing it (see subscription's
  // compedTier computation above). Unlocks unconditionally, same as an active trial but with no
  // time check at all. Deliberate side effect: comping a company to a lower tier now LOCKS
  // features outside that tier's list where today (no engagement at all) they'd be unrestricted
  // — a comp is meant to act exactly like a real tier grant, not a blanket unlock.
  const isLockedByTier = (key) => {
    const trialActive = subscription.trialTier && subscription.trialEndsAt
      && subscription.trialEndsAt.toMillis() > Date.now();
    const trialConfig = subscription.trialTier ? SUBSCRIPTION_TIERS[subscription.trialTier] : null;
    const tierConfig = subscription.tier ? SUBSCRIPTION_TIERS[subscription.tier] : null;
    const compConfig = subscription.compedTier ? SUBSCRIPTION_TIERS[subscription.compedTier] : null;

    if (compConfig && compConfig.features.includes(key)) return false;
    if (trialActive && trialConfig?.features.includes(key)) return false;
    if (tierConfig && subscription.status === 'active' && tierConfig.features.includes(key)) return false;

    // Locked once there's a RECOGNIZED tier engagement (a real subscription, a trial that's
    // active or merely hasn't been cleared yet by expireTrials, or a comp) that simply doesn't
    // cover this particular key — an unrecognized tier/trial/comp id (typo, or a tier later
    // removed from SUBSCRIPTION_TIERS) fails open instead of silently locking everyone out.
    if (tierConfig || trialConfig || compConfig) return true;

    return false;
  };

  // Precedence, highest to lowest: (1) global kill switch off wins outright — the emergency stop,
  // (2) a per-company override wins next, deliberately even over tier-lock (so a platform admin
  // can grant a company something beyond their tier, or restrict something their tier would
  // otherwise include), (3) tier-lock, (4) the company's own raw saved flag.
  const effectiveFlags = Object.keys(DEFAULT_FLAGS).reduce((acc, key) => {
    if (platformOverrides[key]) { acc[key] = false; return acc; }
    const companyOverride = companyFeatureOverrides[key];
    if (companyOverride === 'on') { acc[key] = true; return acc; }
    if (companyOverride === 'off') { acc[key] = false; return acc; }
    acc[key] = !!flags[key] && !isLockedByTier(key);
    return acc;
  }, {});

  return (
    <FeatureFlagsContext.Provider
      value={{
        flags: effectiveFlags,
        rawFlags: flags,
        loading,
        setFlag,
        subscriptionTier: subscription.tier,
        subscriptionStatus: subscription.status,
        trialTier: subscription.trialTier,
        trialEndsAt: subscription.trialEndsAt,
        compedTier: subscription.compedTier,
        isLockedByTier,
        platformOverrides,
        companyFeatureOverrides
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  return ctx;
}
